import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-relay';
import { T } from 'src/shared/helpers';
import styled, { useTheme } from 'styled-components';
import {
    aiCustomAppsCreateMediaFactoryEmbedMutation,
    aiCustomAppsCreateMediaFactoryEmbedMutation$data
} from '../queries/__generated__/aiCustomAppsCreateMediaFactoryEmbedMutation.graphql';
import { createMediaFactoryEmbedMutation } from '../queries/aiCustomApps';
import { buildMadooBackofficeCss, getMadooEmbedLocale, loadMadooEmbedSdk } from './customAppEmbedParams';
import type { MadooRuntimeBridge } from './customAppEmbedParams';
import { AICustomApp } from './customAppTypes';

const FrameWrapper = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    background: ${(props) => props.theme.app.backgroundColorBase};
`;

const FrameMount = styled.div`
    width: 100%;
    height: 100%;
    flex: 1;

    iframe {
        width: 100%;
        height: 100%;
    }
`;

const StateMessage = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.theme.app.secondary};
    font-size: 13px;
`;

interface CustomAppFrontendProps {
    app: AICustomApp;
}

export const CustomAppFrontend: FC<CustomAppFrontendProps> = ({ app }) => {
    const theme = useTheme();
    const [commitEmbed, isLoading] = useMutation<aiCustomAppsCreateMediaFactoryEmbedMutation>(createMediaFactoryEmbedMutation);
    const [embed, setEmbed] = useState<aiCustomAppsCreateMediaFactoryEmbedMutation$data['createMediaFactoryEmbed'] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const mountRef = useRef<HTMLDivElement | null>(null);
    const bridgeRef = useRef<MadooRuntimeBridge | null>(null);

    const runtimeUrl = useMemo(() => {
        if (!embed?.frontendUrl) return null;
        return embed.frontendUrl;
    }, [embed?.frontendUrl]);

    useEffect(() => {
        setEmbed(null);
        setError(null);
        commitEmbed({
            variables: {
                input: {
                    appId: app.mediaFactoryAppID
                }
            },
            onCompleted: (response) => {
                const payload = response.createMediaFactoryEmbed;
                if (payload?.frontendUrl) {
                    setEmbed(payload);
                } else {
                    setError(T._('Unable to open custom app.', 'Admin'));
                }
            },
            onError: () => setError(T._('Unable to open custom app.', 'Admin'))
        });
    }, [app.aiCustomAppID, commitEmbed]);

    useEffect(() => {
        const mount = mountRef.current;
        if (!runtimeUrl || !embed?.token || !embed.sdkUrl || !mount) return;

        let cancelled = false;
        bridgeRef.current?.destroy();
        bridgeRef.current = null;
        mount.replaceChildren();

        loadMadooEmbedSdk(embed.sdkUrl)
            .then((sdk) => {
                if (cancelled || !mountRef.current) return;
                bridgeRef.current = sdk.create({
                    container: mountRef.current,
                    iframeUrl: runtimeUrl,
                    token: embed.token,
                    locale: getMadooEmbedLocale(),
                    theme: {
                        primaryColor: theme.app.secondary
                    },
                    customCss: buildMadooBackofficeCss(theme),
                    width: '100%',
                    height: '100%',
                    allow: 'clipboard-read; clipboard-write',
                    showName: false,
                    showBranding: false
                });
            })
            .catch(() => setError(T._('Unable to open custom app.', 'Admin')));

        return () => {
            cancelled = true;
            bridgeRef.current?.destroy();
            bridgeRef.current = null;
            mount.replaceChildren();
        };
    }, [embed?.sdkUrl, embed?.token, runtimeUrl, theme]);

    if (error) {
        return (
            <FrameWrapper>
                <StateMessage>{error}</StateMessage>
            </FrameWrapper>
        );
    }

    if (isLoading || !runtimeUrl) {
        return (
            <FrameWrapper>
                <StateMessage>{T._('Loading custom app...', 'Admin')}</StateMessage>
            </FrameWrapper>
        );
    }

    return (
        <FrameWrapper>
            <FrameMount ref={mountRef} aria-label={app.name} />
        </FrameWrapper>
    );
};
